import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";

/** P3-5 — one photo per delivered room (spread) or drop (bulk). Upload failures never block: the
 *  target simply stays "needs photo" and the sign-off can go through as "photos pending". */
export function DeliveryPhotoStrip({ ticketId, projectId, actor, targets, pins = [], onChanged, testId = "delivery-photos" }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [forKey, setForKey] = useState("");
  const [busy, setBusy] = useState("");
  const list = targets?.targets || [];
  if (!list.length) return null;
  const pinFor = (t) => pins.find((p) => p.room_key && p.room_key === t.room_key);
  const pick = (key) => { setForKey(key); inputRef.current?.click(); };
  const upload = async (file) => {
    if (!file || !forKey) return;
    const key = forKey;
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("project_id", projectId); fd.append("uploader", actor || ""); fd.append("file", file);
      const { data: photo } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/delivery/tickets/${ticketId}/target-photo`, { target_key: key, storage_path: photo.storage_path,
        filename: photo.filename || "", content_type: photo.content_type || "", by: actor || "" });
      toast.success("Photo saved");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Photo did not upload — it stays pending, the delivery can still be signed off"); }
    finally { setBusy(""); setForKey(""); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="mx-2 mt-2 rounded border border-border bg-card px-2 py-1.5" data-testid={testId}>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`${testId}-input`}
             onChange={(e) => upload(e.target.files?.[0])} />
      <div className="flex items-center gap-2 text-[11px]">
        <Camera className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-semibold text-foreground">Photos</span>
        <span className={`${targets.missing ? "text-amber-800 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"}`} data-testid={`${testId}-summary`}>
          {targets.missing ? `${targets.missing} of ${targets.total} location${targets.total === 1 ? "" : "s"} still need a photo` : `every location has a photo (${targets.total})`}
        </span>
      </div>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {list.map((t) => {
          const pin = pinFor(t);
          const ok = t.photos.length > 0;
          return (
            <li key={t.key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                  ok ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                     : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-200"}`}
                data-testid={`${testId}-target-${t.key.replace(/[^A-Za-z0-9]+/g, "-")}`} data-photos={t.photos.length}>
              {ok ? <CheckCircle2 className="h-3 w-3" /> : null}
              <span className="font-medium">{t.kind === "drop" ? "Drop" : "Room"} · {t.label}</span>
              <span className="text-muted-foreground tabular-nums">{t.qty % 1 ? t.qty.toFixed(1) : t.qty}</span>
              <button type="button" onClick={() => pick(t.key)} disabled={!!busy} title={ok ? "Add another photo" : "Take the photo for this location"}
                      data-testid={`${testId}-shoot-${t.key.replace(/[^A-Za-z0-9]+/g, "-")}`}
                      className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10">
                {busy === t.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
              </button>
              {ok && <span className="text-[10px] text-muted-foreground">{t.photos.length}</span>}
              {pin && (
                <button type="button" onClick={() => navigate(`/drawings?project=${projectId}&markup=${pin.markup_id}`)}
                        data-testid={`${testId}-pin-${t.key.replace(/[^A-Za-z0-9]+/g, "-")}`} title={`Pinned on ${pin.sheet_number}`}
                        className="inline-flex items-center gap-0.5 text-[10px] font-medium text-sky-700 hover:underline dark:text-sky-400">
                  <MapPin className="h-3 w-3" /> on plan
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The sign-off hit "photos_required": take them now, or close with photos pending. */
export function PhotosRequiredDialog({ gate, onClose, onPending }) {
  if (!gate) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="delivery-photos-gate">
        <DialogHeader>
          <DialogTitle className="text-base">Photos missing</DialogTitle>
          <DialogDescription className="text-xs">{gate.message} Each delivered room or drop needs one photo of where the material sits.</DialogDescription>
        </DialogHeader>
        <ul className="max-h-48 space-y-0.5 overflow-y-auto text-xs" data-testid="delivery-photos-gate-list">
          {(gate.targets || []).map((t) => <li key={t.key} className="flex justify-between gap-2"><span>{t.kind === "drop" ? "Drop" : "Room"} · {t.label}</span><span className="tabular-nums text-muted-foreground">{t.qty}</span></li>)}
        </ul>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="delivery-photos-gate-back">Take the photos</Button>
          <Button size="sm" onClick={onPending} data-testid="delivery-photos-gate-pending">Photos pending — close anyway</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
